import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useRef } from 'react';
import BranchForm, {
  BranchFormRef,
  BranchFormValues,
} from '../components/BranchForm';
import { useCreateAgencyBranchById } from '@nxsys/hooks/api/agency';
import { CreateAgencyBranchParams } from '@nxsys/types/api';
import { showMessage } from '@nxsys/utils/message';

type AddBranchModalProps = {
  isOpen: boolean;
  toggle: () => void;
  agencyData: IAgency;
  refetchData: () => void;
};

const AddBranchModal: React.FC<AddBranchModalProps> = ({
  isOpen,
  toggle,
  agencyData,
  refetchData,
}) => {
  const { messages } = useTypedIntl();
  const agencyId = agencyData.id;

  const { run: createAgencyBranchById, loading: isCreatingAgencyBranch } =
    useCreateAgencyBranchById();

  const formRef = useRef<BranchFormRef>(null);
  const handleSubmit = async (values: BranchFormValues) => {
    if (!agencyId) return;
    const params: CreateAgencyBranchParams = {
      siteName: values.siteName,
      code: values.code,
      VATCode: values.VATCode?.value ?? null,
      siteManager: values.siteManager?.value ?? null,
      isSameAsAgency: values.isSameAsAgency,
    };
    if (values.isSameAsAgency) {
      params.address = (agencyData.address || []).map((v) => v ?? '');
      params.city = agencyData.city;
      params.county = agencyData.county;
      params.postCode = agencyData.postCode;
      if (agencyData.country) params.country = agencyData.country;
    } else {
      params.companyName = values.companyName;
      params.address = [values.address1 ?? '', values.address2 ?? '', values.address3 ?? ''];
      params.city = values.city;
      params.county =
        typeof values.county === 'object'
          ? (values.county as any)?.value ?? ''
          : values.county ?? '';
      params.postCode = values.postCode;
      params.country = values.country?.value ?? '';
    }
    createAgencyBranchById(agencyId, params).then(() => {
      refetchData();
      toggle();
      showMessage({
        type: 'success',
        content: messages['common.createAgencyBranchSuccess'],
      });
    });
  };

  return (
    <AppModalContainer
      title={messages['common.newSite']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.confirm'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isCreatingAgencyBranch,
          disabled: isCreatingAgencyBranch,
        },
      ]}
      width={800}
    >
      <BranchForm formRef={formRef} onSubmit={handleSubmit} agencyId={agencyId} />
    </AppModalContainer>
  );
};

export default AddBranchModal;
